// API用户档案管理服务 - 基于HTTP API的CRUD操作
import { 
  UserRecord, 
  UserCard, 
  CreateUserRecordRequest, 
  AssessmentRecord,
  ComprehensiveAssessmentResult 
} from '../types/index';
import { apiConfigManager } from '../config/apiConfig';
import { authService } from './authService';

// API响应接口
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message: string;
  timestamp: string;
}

class ApiUserService {
  
  // HTTP请求封装
  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = apiConfigManager.buildApiUrl(endpoint);

    try {
      console.log(`🌐 [ApiUserService.request] API请求 [FastAPI]: ${options.method || 'GET'} ${url}`);

      // 获取认证头
      const authHeaders = authService.getAuthHeaders();

      const finalHeaders = {
        ...authHeaders,
        ...options.headers,
      };

      console.log('📤 [ApiUserService.request] 最终headers:', Object.keys(finalHeaders));

      const response = await fetch(url, {
        headers: finalHeaders,
        ...options,
      });

      console.log('📥 [ApiUserService.request] 响应状态:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        console.error('❌ [ApiUserService.request] 请求失败 - 响应非OK');
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [ApiUserService.request] 错误详情:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ApiResponse<T> = await response.json();

      console.log('📋 [ApiUserService.request] 响应数据:', {
        success: data.success,
        message: data.message,
        hasData: !!data.data,
        dataKeys: data.data ? Object.keys(data.data) : 'no-data',
        fullData: data.data
      });

      if (!data.success) {
        throw new Error(data.message || 'API请求失败');
      }
      
      console.log(`✅ API响应成功: ${data.message}`);
      return data.data as T;
    } catch (error) {
      console.error(`❌ API请求失败 ${url}:`, error);
      
      // 如果API服务不可用，抛出特定错误
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('无法连接到ICOPE服务器，请确认后端服务已启动');
      }
      
      throw error;
    }
  }

  // 检查API服务可用性
  async checkApiHealth(): Promise<boolean> {
    try {
      await this.request('/health');
      return true;
    } catch (error) {
      console.warn('⚠️ API健康检查失败:', error);
      return false;
    }
  }

  // 创建用户档案
  async createUserRecord(request: CreateUserRecordRequest): Promise<string> {
    try {
      const user = await this.request<UserRecord>('/users', {
        method: 'POST',
        body: JSON.stringify(request),
      });

      console.log('✅ API用户档案创建成功:', user.id);
      return user.id;
    } catch (error) {
      console.error('❌ 创建用户档案失败:', error);
      throw error;
    }
  }

  // 获取所有活跃用户的卡片信息
  async getAllActiveUsers(): Promise<UserCard[]> {
    try {
      const userCards = await this.request<UserCard[]>('/users');
      
      console.log(`📋 获取到 ${userCards.length} 个API用户档案`);
      return userCards;
    } catch (error) {
      console.error('❌ 获取用户列表失败:', error);
      throw error;
    }
  }

  // 获取单个用户完整档案
  async getUserRecord(userId: string): Promise<UserRecord | null> {
    try {
      const user = await this.request<UserRecord>(`/users/${userId}`);
      return user;
    } catch (error) {
      console.error('❌ 获取用户档案失败:', error);
      // 所有错误都抛出，让unifiedUserService处理fallback
      throw error;
    }
  }

  // 获取单个用户完整档案的别名方法（为了兼容性）
  async getUserById(userId: string): Promise<UserRecord | null> {
    return this.getUserRecord(userId);
  }

  // 更新用户档案基本信息
  async updateUserRecord(userId: string, updates: Partial<UserRecord>): Promise<void> {
    try {
      await this.request(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      
      console.log('✅ API用户档案更新成功:', userId);
    } catch (error) {
      console.error('❌ 更新用户档案失败:', error);
      throw error;
    }
  }

  // 为用户添加评估结果
  async addAssessmentResult(
    userId: string, 
    result: ComprehensiveAssessmentResult, 
    notes?: string,
    conductedBy?: string
  ): Promise<void> {
    try {
      const assessmentData = {
        result,
        notes: notes || '',
        conductedBy: conductedBy || 'user',
        duration: null // 可以从前端传递
      };

      await this.request(`/assessments/user/${userId}`, {
        method: 'POST',
        body: JSON.stringify(assessmentData),
      });

      console.log('✅ API评估结果添加成功:', userId);
    } catch (error) {
      console.error('❌ 添加评估结果失败:', error);
      throw error;
    }
  }

  // 获取用户评估历史
  async getAssessmentHistory(userId: string): Promise<AssessmentRecord[]> {
    try {
      const assessments = await this.request<AssessmentRecord[]>(`/assessments/user/${userId}`);
      return assessments || [];
    } catch (error) {
      console.error('❌ 获取评估历史失败:', error);
      throw error;
    }
  }

  // 根据ID获取单个评估记录
  async getAssessmentById(assessmentId: string): Promise<AssessmentRecord> {
    try {
      console.log(`🔍 获取评估记录: ${assessmentId}`);
      const assessment = await this.request<AssessmentRecord>(`/assessments/${assessmentId}`);
      console.log('✅ 评估记录获取成功:', assessmentId);
      return assessment;
    } catch (error) {
      console.error('❌ 获取评估记录失败:', error);
      throw error;
    }
  }

  // 根据用户ID获取所有评估记录
  async getAssessmentsByUserId(userId: string): Promise<AssessmentRecord[]> {
    try {
      console.log(`🔍 获取用户评估记录列表: ${userId}`);
      // 修复API路径：使用正确的Rails路由
      const assessments = await this.request<AssessmentRecord[]>(`/assessments/user/${userId}`);
      console.log(`✅ 评估记录获取成功，共 ${assessments.length} 条`);
      return assessments;
    } catch (error) {
      console.error('❌ 获取用户评估记录失败:', error);
      throw error;
    }
  }

  // 根据ID获取单个需求报告
  async getNeedReportById(reportId: string): Promise<any> {
    try {
      console.log(`🔍 获取需求报告: ${reportId}`);
      const report = await this.request<any>(`/data/need-reports/detail/${reportId}`);
      console.log('✅ 需求报告获取成功:', reportId);
      return report;
    } catch (error) {
      console.error('❌ 获取需求报告失败:', error);
      throw error;
    }
  }

  // 删除用户档案（软删除，标记为inactive）
  async deactivateUserRecord(userId: string): Promise<void> {
    try {
      await this.request(`/users/${userId}`, {
        method: 'DELETE',
      });
      
      console.log('✅ API用户档案已停用:', userId);
    } catch (error) {
      console.error('❌ 停用用户档案失败:', error);
      throw error;
    }
  }

  // 彻底删除用户档案（慎用）
  async deleteUserRecord(userId: string): Promise<void> {
    // 使用软删除替代
    return this.deactivateUserRecord(userId);
  }

  // 搜索用户（按姓名）
  async searchUsers(searchTerm: string): Promise<UserCard[]> {
    try {
      const users = await this.request<UserCard[]>(`/users/search/${encodeURIComponent(searchTerm)}`);
      
      console.log(`🔍 API搜索 "${searchTerm}" 找到 ${users.length} 个结果`);
      return users;
    } catch (error) {
      console.error('❌ 搜索用户失败:', error);
      throw error;
    }
  }

  // 生成默认头像URL（基于姓名）
  generateDefaultAvatar(name: string, gender: '男' | '女'): string {
    // 简单的默认头像生成逻辑
    const baseUrl = 'https://api.dicebear.com/7.x';
    const style = gender === '男' ? 'male' : 'female';
    return `${baseUrl}/${style}/svg?seed=${encodeURIComponent(name)}`;
  }

  // 数据导出功能
  async exportAllData(): Promise<any> {
    try {
      const exportData = await this.request('/data/export');
      console.log('📤 数据导出成功');
      return exportData;
    } catch (error) {
      console.error('❌ 数据导出失败:', error);
      throw error;
    }
  }

  // 数据导入功能
  async importData(data: any): Promise<void> {
    try {
      await this.request('/data/import', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      console.log('📥 数据导入成功');
    } catch (error) {
      console.error('❌ 数据导入失败:', error);
      throw error;
    }
  }

  // 创建备份
  async createBackup(): Promise<string> {
    try {
      const backupInfo = await this.request<{ backupName: string }>('/data/backup', {
        method: 'POST',
      });
      
      console.log('💾 备份创建成功:', backupInfo.backupName);
      return backupInfo.backupName;
    } catch (error) {
      console.error('❌ 创建备份失败:', error);
      throw error;
    }
  }

  // 获取系统状态
  async getSystemStatus(): Promise<any> {
    try {
      const status = await this.request('/data/status');
      return status;
    } catch (error) {
      console.error('❌ 获取系统状态失败:', error);
      throw error;
    }
  }

  // localStorage数据迁移
  async migrateFromLocalStorage(localStorageData: any): Promise<any> {
    try {
      const result = await this.request('/data/migrate-from-localstorage', {
        method: 'POST',
        body: JSON.stringify(localStorageData),
      });

      console.log('🔄 localStorage数据迁移完成:', result);
      return result;
    } catch (error) {
      console.error('❌ localStorage数据迁移失败:', error);
      throw error;
    }
  }

  // ==================== 老年人画像管理 ====================

  /**
   * 批量更新老年人画像字段
   * @param userId 用户ID
   * @param profileFields 画像字段（键值对，带source和updatedAt元数据）
   * @returns 更新后的画像数据和完整度
   */
  async updateElderlyProfile(userId: string, profileFields: Record<string, any>): Promise<{
    success: boolean;
    profile: any;
    completeness: number;
  }> {
    try {
      console.log(`📝 更新老年人画像: ${userId}`, profileFields);

      const result = await this.request<{
        success: boolean;
        profile: any;
        completeness: number;
      }>(`/users/${userId}/update_profile`, {
        method: 'PATCH',
        body: JSON.stringify({ profile: profileFields }),
      });

      console.log(`✅ 画像更新成功: ${userId}, 完整度: ${result.completeness}%`);
      return result;
    } catch (error) {
      console.error('❌ 更新老年人画像失败:', error);
      throw error;
    }
  }

  /**
   * 获取用户画像完整度
   * @param userId 用户ID
   * @returns 完整度百分比、总字段数、archive字段列表
   */
  async getProfileCompleteness(userId: string): Promise<{
    completeness: number;
    total_fields: number;
    archive_fields: string[];
  }> {
    try {
      console.log(`📊 获取画像完整度: ${userId}`);

      const result = await this.request<{
        completeness: number;
        total_fields: number;
        archive_fields: string[];
      }>(`/users/${userId}/profile_completeness`);

      console.log(`✅ 画像完整度: ${result.completeness}%, 总字段数: ${result.total_fields}`);
      return result;
    } catch (error) {
      console.error('❌ 获取画像完整度失败:', error);
      throw error;
    }
  }
}

// 导出单例实例
export const apiUserService = new ApiUserService();
export default ApiUserService;
